import React from "react";
import { formatPhoneNumber } from "./common-util";

/**
 * 검색어를 전화번호 digit 매칭용으로 정규화합니다.
 * - +82 국가코드 제거 후 앞자리 0 복원
 * - 숫자 외 문자 제거
 *
 * ex) "+8210"          → "010"
 * ex) "+821010007248"  → "01010007248"
 * ex) "010-1234"       → "0101234"
 * ex) "100072"         → "100072"
 */
const normalizeSearchToDigits = (searchText: string): string => {
  let value = searchText.replace(/\s/g, "");
  if (value.startsWith("+820")) {
    value = value.slice(3); // "+820..." → "0..."
  } else if (value.startsWith("+82")) {
    value = "0" + value.slice(3); // "+8210..." → "010..."
  }
  return value.replace(/\D/g, "");
};

/**
 * 한국 표기 전화번호 문자열(하이픈 포함)에서
 * digit 인덱스 기준으로 원본 문자열의 시작/끝 인덱스를 반환합니다.
 */
const mapDigitIndexToOriginal = (
  displayStr: string,
  digitStart: number,
  digitLength: number,
): { start: number; end: number } | null => {
  let digitIdx = 0;
  let start = -1;

  for (let i = 0; i < displayStr.length; i++) {
    if (/\d/.test(displayStr[i])) {
      if (digitIdx === digitStart) {
        start = i;
      }
      digitIdx++;
      if (start !== -1 && digitIdx === digitStart + digitLength) {
        return { start, end: i + 1 };
      }
    }
  }

  return null;
};

/**
 * 검색어(searchText)에 해당하는 부분을 소스(source)에서 찾아
 * <span>으로 감싸 반환합니다.
 *
 * - 전화번호: source는 DB 저장값(+82...), 한국식 표기(010-xxxx-xxxx)로 변환 후 하이라이트
 * - 이메일: source 그대로 직접 매칭
 *
 * @param searchText 검색어
 * @param source     DB에서 제공하는 전화번호(+82...) 또는 이메일
 * @param className  강조 span에 적용할 CSS 클래스명 (기본값: "highlight")
 *
 * @example
 * highlightSearchText("100072", "+821010007248")
 * // → <>010-<span className="highlight">1000-72</span>48</>
 *
 * highlightSearchText("+8210", "+821010007248")
 * // → <><span className="highlight">010</span>-1000-7248</>
 *
 * highlightSearchText("user", "user@example.com")
 * // → <><span className="highlight">user</span>@example.com</>
 */
/**
 * 문자열에서 검색어의 모든 매칭 위치를 반환합니다. (문자 인덱스 기준)
 */
const findAllStringMatches = (str: string, search: string): Array<{ start: number; end: number }> => {
  const ranges: Array<{ start: number; end: number }> = [];
  let from = 0;
  while (from < str.length) {
    const index = str.indexOf(search, from);
    if (index === -1) break;
    ranges.push({ start: index, end: index + search.length });
    from = index + search.length;
  }
  return ranges;
};

/**
 * digit 문자열에서 searchDigits의 모든 매칭 위치(digit 인덱스)를 반환합니다.
 */
const findAllDigitMatches = (digitStr: string, searchDigits: string): number[] => {
  const indices: number[] = [];
  let from = 0;
  while (from < digitStr.length) {
    const index = digitStr.indexOf(searchDigits, from);
    if (index === -1) break;
    indices.push(index);
    from = index + searchDigits.length;
  }
  return indices;
};

/**
 * 매칭 범위 배열을 기반으로 문자열을 JSX로 조합합니다.
 */
const buildHighlightJsx = (
  str: string,
  ranges: Array<{ start: number; end: number }>,
  className: string,
): JSX.Element => {
  if (ranges.length === 0) return <>{str}</>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  ranges.forEach(({ start, end }, i) => {
    if (cursor < start) parts.push(str.slice(cursor, start));
    parts.push(<span key={i} className={className}>{str.slice(start, end)}</span>);
    cursor = end;
  });

  if (cursor < str.length) parts.push(str.slice(cursor));

  return <>{parts}</>;
};

/**
 * 검색어(searchText)에 해당하는 부분을 소스(source)에서 찾아
 * <span>으로 감싸 반환합니다. 매칭되는 모든 위치에 하이라이트 적용.
 *
 * - 전화번호: source는 DB 저장값(+82...), 한국식 표기(010-xxxx-xxxx)로 변환 후 하이라이트
 * - 이메일: source 그대로 직접 매칭
 *
 * @param searchText 검색어
 * @param source     DB에서 제공하는 전화번호(+82...) 또는 이메일
 * @param className  강조 span에 적용할 CSS 클래스명 (기본값: "highlight")
 *
 * @example
 * highlightSearchText({ searchText: "01", source: "+821011000011" })
 * // display: "010-1100-0011"
 * // → <><span>01</span><span>0-1</span>100-0<span>01</span>1</>
 * //   digit "01" 매칭: [0~1]→"01", [2~3]→"0-1"(하이픈 포함), [8~9]→"01"
 *
 * highlightSearchText({ searchText: "100072", source: "+821010007248" })
 * // → <>010-<span>1000-72</span>48</>
 *
 * highlightSearchText({ searchText: "user", source: "user@example.com" })
 * // → <><span>user</span>@example.com</>
 */
export const highlightSearchText = ({
  searchText,
  source,
  className = "text-highlight",
}: {
  searchText: string;
  source: string;
  className?: string;
}): JSX.Element => {
  if (!searchText || !source) return <>{source}</>;

  // 이메일: @ 포함 여부로 판단, 직접 매칭
  if (source.includes("@")) {
    const lowerSource = source.toLowerCase();
    const lowerSearch = searchText.toLowerCase().trim();

    if (!lowerSearch) return <>{source}</>;

    const ranges = findAllStringMatches(lowerSource, lowerSearch);
    if (ranges.length === 0) return <>{source}</>;

    return buildHighlightJsx(source, ranges, className);
  }

  // 전화번호: DB 저장값(+821010007248) → 한국 표기(010-1000-7248) 변환 후 digit 기준 매칭
  const display = formatPhoneNumber(source) ?? source;
  const displayDigits = display.replace(/\D/g, "");
  const searchDigits = normalizeSearchToDigits(searchText);

  if (!searchDigits) return <>{display}</>;

  // +82로 시작하는 검색어는 번호 앞자리를 의미하므로 시작 위치(0)에서만 매칭
  const digitMatchIndices = searchText.trimStart().startsWith("+82")
    ? (displayDigits.startsWith(searchDigits) ? [0] : [])
    : findAllDigitMatches(displayDigits, searchDigits);
  if (digitMatchIndices.length === 0) return <>{display}</>;

  const ranges = digitMatchIndices
    .map((digitStart) => mapDigitIndexToOriginal(display, digitStart, searchDigits.length))
    .filter((r): r is { start: number; end: number } => r !== null);

  return buildHighlightJsx(display, ranges, className);
};
